import { useCallback, useEffect, useMemo, useState } from "react";
import { useAtom } from "jotai";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
  DialogActions,
  IconButton,
} from "@mui/material";
import { SelectFromList } from "../../components/SelectFromList";
import { CloseButton } from "../../components/buttons/CloseButton";
import { ProcessButton } from "../../components/buttons/ProcessButton";
import { IconTypes } from "../../components/icons.types";
import { sessionAtom } from "../../features/accounts/account.state";
import { CatalogPrivacyLevels } from "../../features/catalogs/catalog.defs";
import {
  Catalog,
  CatalogStatus,
  PRIVACY_PROTECTED,
} from "../../features/catalogs/catalogs";
import { validatePermalink } from "../../features/data/data-types";
import { StoreRef } from "../../features/stores/store";
import { Transition } from "../../components/Transitions";
import { trackEvent } from "../../features/analytics/analytics";
import { useDialogs } from "../../contexts/useDialogs";
import { journeyManager } from "../../features/guide/journeyManager";
import {
  loadPrePublishCheck,
  loadPrePublishCutTextCheck,
  PrePublishCheck,
} from "../../features/catalogs/catalog.service";
import { PrePublishCheckPanel } from "./PrePublishCheckPanel";
import {
  OutputForkSurface,
  answerOutputFork,
  takeOutputFork,
} from "../../features/catalogs/outputFork";
import { OutputForkPublish } from "./OutputForkPublish";

type CatalogPublishModalProps = {
  isOpen?: boolean;
  catalog: Catalog;
  onClose: () => void;
  onChange: (catalog: Catalog) => void;
  stores: StoreRef[];
  /** S117: set where a first publish may turn into the "Who is it for?" fork. Unset = never (Concierge). */
  forkSurface?: OutputForkSurface;
};

export const CatalogPublishModal = ({
  catalog,
  onClose,
  isOpen,
  onChange,
  stores,
  forkSurface,
}: CatalogPublishModalProps) => {
  const [session] = useAtom(sessionAtom);
  const [edited, setEdited] = useState({ ...catalog });
  const [permalink, setPermalink] = useState<string>();
  const [isProcessing, setIsProcessing] = useState(false);

  const { openUpgradeModal } = useDialogs();

  // S117 step 1 - after an account's first publish the dialog becomes the fork instead of closing.
  const [forkOpen, setForkOpen] = useState(false);
  const [forkAnswered, setForkAnswered] = useState(false);

  // S29 — the two-door recovery check. Flag-gated server-side; a null result (flag off, or the
  // call failed) means the dialog behaves exactly as it did before this shipped.
  const [check, setCheck] = useState<PrePublishCheck | null>(null);
  const [overridden, setOverridden] = useState(false);

  useEffect(() => {
    if (!isOpen || !catalog.id) return;
    let cancelled = false;
    setOverridden(false);
    loadPrePublishCheck(catalog.id).then((result) => {
      if (cancelled) return;
      setCheck(result);
      if (!result?.enabled) return;
      // S58 Ph B — cut-off text needs a Chrome layout, so it lands seconds later and joins the list.
      loadPrePublishCutTextCheck(catalog.id).then((cut) => {
        if (cancelled || !cut?.enabled || cut.findings.length === 0) return;
        setCheck((prev) =>
          prev
            ? {
                ...prev,
                clean: false,
                blockerCount: prev.blockerCount + cut.blockerCount,
                warningCount: prev.warningCount + cut.warningCount,
                findings: [...prev.findings, ...cut.findings],
              }
            : prev
        );
      });
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, catalog.id]);

  const blockerCount =
    check?.enabled === true
      ? check.findings.filter((f) => f.severity === "blocker").length
      : 0;

  // The halt variant is the ONLY thing that can withhold publish, and only until the user picks a
  // door. Gate on blockers, never on any score (memory/features/design-quality/log.md 2026-07-27).
  const publishHeld =
    check?.mode === "halt" && blockerCount > 0 && !overridden;

  const permalinkError = useMemo(() => {
    return validatePermalink(permalink);
  }, [permalink]);

  const handlePublish = useCallback(
    async (status: CatalogStatus) => {
      setIsProcessing(true);
      edited.status = status;
      await onChange(edited);

      trackEvent({
        category: "catalog",
        action: status === "Published" ? "publish" : "unpublish",
        // Publish was the only catalog act with no catalog identity, so a publish could never be
        // attributed to the catalog it belonged to. Same "<id> : <name>" shape as create/update.
        label: edited.id + " : " + edited.name,
        tag: true,
        feature: status === "Published",
      });

      if (status === "Published")
        journeyManager.updateJourneyProgress("onboarding", "catalog_published");

      const showFork =
        status === "Published" && !!forkSurface && (await takeOutputFork(forkSurface));
      setIsProcessing(false);
      if (showFork) {
        setForkOpen(true);
        return;
      }

      onClose();
    },
    [edited, forkSurface, onChange, onClose]
  );

  const closeFork = useCallback(() => {
    if (!forkAnswered && forkSurface) answerOutputFork(forkSurface, "dismissed");
    onClose();
  }, [forkAnswered, forkSurface, onClose]);

  // useEffect(() => {
  //   const getStores = async () => {
  //     const s = await getStoreDb();
  //     setStores(s);
  //   };
  //   getStores();
  // }, []);

  const storeList = useMemo(() => {
    return stores?.map((s) => ({
      name:
        s.store.name +
        " (" +
        (s.store.productCount ?? 0) +
        ") - " +
        s.store.privacyLevel,
      value: s.store.id,
    }));
  }, [stores]);

  const path = session.account.config.catalogPath + "/catalogs/";
  const isPublished = edited.status === "Published";

  const setPrivacy = (privacy: number) => {
    if (privacy >= PRIVACY_PROTECTED && !session.account.isProPlan) {
      openUpgradeModal({
        title: "Protected catalogs are available for Pro accounts only.",
        entryPoint: "catalog-publish-protected-pro",
      });
      return;
    }
    setEdited({ ...edited, privacy });
  };

  if (forkOpen) {
    return (
      <Dialog
        open={isOpen}
        onClose={closeFork}
        maxWidth="md"
        fullWidth
        TransitionComponent={Transition}
      >
        <DialogTitle>
          Your catalog is live <CloseButton onClose={closeFork} />
        </DialogTitle>
        <DialogContent>
          <OutputForkPublish
            link={path + edited.permalink + ".html"}
            onAnswered={() => setForkAnswered(true)}
            onDone={onClose}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Transition}
    >
      <DialogTitle>
        Publish {catalog.name} <CloseButton onClose={onClose} />
      </DialogTitle>
      <DialogContent>
        {check && (
          <PrePublishCheckPanel
            check={check}
            overridden={overridden}
            onOverride={() => {
              setOverridden(true);
              trackEvent({
                category: "catalog",
                action: "prepublish-override",
                label: catalog.id,
              });
            }}
            onFix={() => {
              trackEvent({
                category: "catalog",
                action: "prepublish-fix",
                label: catalog.id,
              });
              onClose();
            }}
          />
        )}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography>
            <b>Permalink: </b>
            {permalink ? "" : path + edited.permalink}
          </Typography>
          {!permalink && (
            <>
              <Button
                size="small"
                onClick={() => setPermalink(catalog.permalink)}
              >
                Edit
              </Button>
              {edited.permalink === catalog.permalink && (
                <IconButton
                  size="small"
                  title={"Open the catalog site in the new tab"}
                  href={path + edited.permalink + ".html"}
                  target="_blank"
                >
                  <IconTypes.OpenInNew />
                </IconButton>
              )}
            </>
          )}
          {permalink && (
            <>
              <TextField
                size="small"
                sx={{ mx: 2 }}
                required
                value={permalink}
                onChange={(e) => setPermalink(e.target.value)}
                error={!!permalinkError}
                helperText={permalinkError}
              />
              <Button
                size="small"
                onClick={() => {
                  setEdited({ ...edited, permalink });
                  trackEvent({
                    category: "catalog",
                    action: "permalink",
                    label: permalink,
                  });
                  setPermalink(null);
                }}
                disabled={!!permalinkError}
              >
                Apply
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setPermalink(null);
                }}
              >
                Cancel
              </Button>
            </>
          )}
        </Box>
        <Box>
          <Typography variant="h6" sx={{ my: 2 }}>
            Settings
          </Typography>
          <Box sx={{ mb: 2 }}>
            <SelectFromList
              label="Privacy"
              size="small"
              entries={CatalogPrivacyLevels}
              value={edited.privacy ?? 0}
              setValue={(c) => {
                setPrivacy(c);
              }}
              helpText={CatalogPrivacyLevels[edited.privacy ?? 0].d}
            />
            {edited.privacy === PRIVACY_PROTECTED && (
              <TextField
                sx={{ ml: 2 }}
                label="Access code"
                size="small"
                value={edited.accessCode ?? ""}
                onChange={(e) =>
                  setEdited({ ...edited, accessCode: e.target.value })
                }
              />
            )}
          </Box>

          <FormGroup sx={{ display: "flex", gap: 1, flexDirection: "row" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={edited.pdf}
                  onChange={(e) => {
                    setEdited({ ...edited, pdf: e.target.checked });
                  }}
                />
              }
              label="Enable PDF"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={edited.html}
                  onChange={(e) => {
                    setEdited({ ...edited, html: e.target.checked });
                  }}
                />
              }
              label="Enable HTML"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={edited.social}
                  onChange={(e) => {
                    setEdited({ ...edited, social: e.target.checked });
                  }}
                />
              }
              label="Show social buttons"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={edited.hideOther}
                  onChange={(e) => {
                    setEdited({ ...edited, hideOther: e.target.checked });
                  }}
                />
              }
              label="Hide other public catalogs"
            />
          </FormGroup>
          <FormGroup
            sx={{
              display: "flex",
              gap: 1,
              flexDirection: "row",
              mt: 3,
              alignItems: "center",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={edited.storeLinked}
                  onChange={(e) => {
                    setEdited({ ...edited, storeLinked: e.target.checked });
                  }}
                />
              }
              label="Link Showroom"
            />
            {edited.storeLinked && (
              <>
                <SelectFromList
                  label="Showroom"
                  size="small"
                  entries={storeList}
                  value={edited.storeId ?? 0}
                  setValue={(storeId) => {
                    setEdited({ ...edited, storeId });
                  }}
                />
                <Box>
                  {" "}
                  <TextField
                    label="Button text"
                    size="small"
                    value={edited.storeButton ?? ""}
                    onChange={(e) =>
                      setEdited({ ...edited, storeButton: e.target.value })
                    }
                  />
                </Box>
              </>
            )}
          </FormGroup>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <ProcessButton
          label={isPublished ? "Update" : "Publish"}
          color={isPublished ? "primary" : "success"}
          processing={isProcessing}
          disabled={!!permalink || publishHeld}
          onClick={() => handlePublish("Published")}
          startIcon={isPublished ? null : <IconTypes.Play />}
        />
        {edited.status === "Published" && (
          <ProcessButton
            label="Unpublish"
            variant="outlined"
            color="primary"
            processing={isProcessing}
            disabled={!!permalink}
            onClick={() => handlePublish("Draft")}
            startIcon={<IconTypes.Pause />}
          />
        )}
      </DialogActions>
    </Dialog>
  );
};
